import { getApiUrl, jobsPath } from '@/config/api';
import { axiosInstance } from '@/services/api/axios';
import { store } from '@/app/store';
import { isTokenExpired } from '@/utils/token';
import { getDeviceSignature } from '@/utils/deviceSignature';
import type {
  JobPosting,
  JobPostingListItem,
  JobPostingsResponse,
  JobApplication,
  JobApplicationResponse,
} from '@/services/api/types';

// Backend caps `limit` at 100 and sorts newest-first by default. Page size is
// maxed out so the common case (a few dozen open roles) is a single request;
// fetchAllOpen only makes extra round-trips when more than one page exists.
const PUBLIC_JOBS_PAGE_SIZE = 100;
// Safety bound on the pagination loop so a very large board cannot trigger an
// unbounded fan-out of requests. Beyond this the list is flagged `truncated`.
const PUBLIC_JOBS_MAX_PAGES = 20;

// Candidate token + device signature when a valid session exists, so the BE
// (optionalCandidateAuth) returns per-item `hasApplied`. Anonymous still works.
const buildPublicJobHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = store.getState().auth.tokens.accessToken;
  if (token && !isTokenExpired(token)) {
    headers.Authorization = `Bearer ${token}`;
    headers['X-Device-Signature'] = getDeviceSignature();
  }
  return headers;
};

export const jobsApi = {
  /** Single page of public job postings (defaults to the BE's first page). */
  getAll: async (params?: { page?: number; limit?: number }): Promise<JobPostingsResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    // Org-addressed when this site declares a tenant, bare otherwise — see
    // `jobsPath`. Same three endpoints exist on both surfaces.
    const response = await fetch(getApiUrl(jobsPath('', qs)), {
      headers: buildPublicJobHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to load jobs. Please try again.');
    }
    return response.json();
  },

  /**
   * All open postings across every page (bounded by PUBLIC_JOBS_MAX_PAGES) so
   * the jobs page's client-side search / filters / sort / "show all" operate
   * over the full set instead of only the BE's first page. `truncated` is true
   * when the board exceeds the page cap and some postings were not loaded.
   */
  fetchAllOpen: async (): Promise<{ jobs: JobPostingListItem[]; total: number; truncated: boolean }> => {
    const first = await jobsApi.getAll({ page: 1, limit: PUBLIC_JOBS_PAGE_SIZE });
    const jobs: JobPostingListItem[] = Array.isArray(first.data) ? [...first.data] : [];
    const totalPages = first.pagination?.totalPages ?? 1;
    const total = first.pagination?.total ?? jobs.length;

    const lastPage = Math.min(totalPages, PUBLIC_JOBS_MAX_PAGES);
    if (lastPage > 1) {
      const restPages = Array.from({ length: lastPage - 1 }, (_, i) => i + 2);
      const rest = await Promise.all(
        restPages.map((page) => jobsApi.getAll({ page, limit: PUBLIC_JOBS_PAGE_SIZE })),
      );
      for (const r of rest) {
        if (Array.isArray(r.data)) jobs.push(...r.data);
      }
    }

    return { jobs, total, truncated: totalPages > PUBLIC_JOBS_MAX_PAGES };
  },

  getById: async (jobId: string): Promise<{ success: boolean; data: JobPosting }> => {
    // Attach the candidate token when it's present and still valid so the BE
    // (optionalCandidateAuth) returns `candidateApplicationContext.hasApplied`.
    // Done with a manual header rather than axiosInstance to avoid its
    // expired-token logout side-effect on this otherwise-public page.
    const response = await fetch(getApiUrl(jobsPath(`/${jobId}`)), {
      headers: buildPublicJobHeaders(),
    });
    if (response.status === 404) {
      // Posting is unpublished, removed, or past its deadline.
      const err = new Error('This position is no longer available.') as Error & { code?: string };
      err.code = 'JOB_UNAVAILABLE';
      throw err;
    }
    if (!response.ok) {
      throw new Error('Failed to load job details. Please try again.');
    }
    return response.json();
  },

  apply: async (
    jobId: string,
    application: JobApplication,
    _accessToken?: string,
    resumeFile?: File | null
  ): Promise<JobApplicationResponse> => {
    const payload = new FormData();

    if (resumeFile) {
      payload.append('resume', resumeFile);
    }

    for (const [key, value] of Object.entries(application)) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'object') {
        payload.append(key, JSON.stringify(value));
      } else {
        payload.append(key, String(value));
      }
    }

    const response = await axiosInstance.post<JobApplicationResponse>(
      jobsPath(`/${jobId}/apply`),
      payload,
      { headers: { 'Content-Type': undefined as unknown as string } }
    );

    return response.data;
  },
};
